<template>
    <m-pageframe title="{organization_label}: Opt-in Analytics">
        <!-- TODO: remove sys admin check for final state -->
        <v-container
            v-if="$rc.$state.isAuthActorSystemAdmin()"
            class="analytics-container"
            fluid
        >
            <div class="subcontainer">
                <link-back
                    class="mb-4"
                    :fallback_route="{ name: 'all-analytics', params: { organization_id: $rc.getOrganizationId() } }"
                />

                <!-- Header -->
                <div class="d-flex justify-space-between align-center flex-wrap gap-4 mb-4 mb-md-8">
                    <div>
                        <h1 class="mb-0">Opt-in Analytics</h1>
                        <p class="my-0">Insights into consent data for your organization</p>
                    </div>

                    <div class="text-md-right">
                        <v-btn
                            color="primary"
                            :loading="downloadLoading"
                            @click="downloadConsentRegister"
                        >
                            <v-icon
                                aria-hidden="true"
                                left
                            >mdi-download</v-icon>
                            Export Data
                        </v-btn>
                        <div class="text-caption grey--text mt-2">
                            Updated {{ lastUpdated }}
                        </div>
                    </div>
                </div>

                <!-- Key Metrics -->
                <section class="metrics-grid mb-8">
                    <div class="metric-card rounded-lg">
                        <div class="metric-number">{{ totalConsentCount.toLocaleString() }}</div>
                        <div class="metric-label">Total Consents</div>
                    </div>

                    <div class="metric-card rounded-lg">
                        <div class="metric-number">{{ optInsLast30Days.toLocaleString() }}</div>
                        <div class="metric-label">Last 30 Days</div>
                    </div>

                    <div class="metric-card rounded-lg">
                        <div class="metric-number">{{ optInsYearToDate.toLocaleString() }}</div>
                        <div class="metric-label">Year to Date</div>
                    </div>

                    <div class="metric-card rounded-lg highlight">
                        <div class="metric-number">{{ monthToDateGrowthFormatted }}</div>
                        <div class="metric-label">Last Month Growth</div>
                    </div>
                </section>

                <!-- Charts -->
                <section class="mb-8">
                    <v-row align="start">
                        <v-col
                            cols="12"
                            lg="8"
                            class="pr-lg-4 align-self-start"
                        >
                            <div class="chart-container rounded-lg">
                                <div class="d-flex justify-space-between align-baseline mb-5">
                                    <h3 class="font-weight-bold text-h6 mb-0">Daily Opt-ins Trend</h3>
                                    <div class="d-flex align-center">
                                        <span class="text-body-2 mr-1">{{ currentTimeSeriesRangeLabel }}</span>
                                        <v-btn
                                            icon
                                            small
                                            color="primary"
                                            aria-label="Edit Time Series Range"
                                            @click="openTimeSeriesRangeDialog"
                                        >
                                            <v-icon
                                                aria-hidden="true"
                                                small
                                            >mdi-pencil</v-icon>
                                        </v-btn>
                                    </div>
                                </div>
                                <m-chart-line
                                    title=""
                                    title_alignment="left"
                                    :labels="timeSeriesLabels"
                                    :data="timeSeriesData"
                                />
                            </div>

                            <div class="chart-container rounded-lg mt-6">
                                <div class="d-flex justify-space-between align-center mb-4">
                                    <h3 class="font-weight-bold text-h6 mb-1">Recent Activity</h3>
                                    <v-btn
                                        color="primary"
                                        text
                                        small
                                        @click="refreshRecentOptIns"
                                        :loading="recentOptInsLoading"
                                    >
                                        <v-icon
                                            size="18"
                                            class="mr-1"
                                            aria-hidden="true"
                                        >mdi-refresh</v-icon>
                                        Refresh Data
                                    </v-btn>
                                </div>

                                <div class="recent-table-container">
                                    <v-data-table
                                        :headers="recentOptInsHeaders"
                                        :items="recentOptIns"
                                        :items-per-page="8"
                                        hide-default-footer
                                        class="recent-table"
                                        :loading="recentOptInsLoading"
                                        :sort-by="['datetime']"
                                        :sort-desc="[true]"
                                    >
                                        <template #item.phone="{ item }">
                                            {{ $rc.pretty(item.phone, 'endpoint_no_parens') }}
                                        </template>

                                        <template #item.datetime="{ item }">
                                            <div>
                                                <div :title="$rc.pretty(item.datetime, 'date')">
                                                    {{ getHumanFriendlyDate(item.datetime) }}
                                                </div>
                                                <div class="text-caption">
                                                    {{ $rc.pretty(item.datetime, 'time') }}
                                                </div>
                                            </div>
                                        </template>

                                        <template #item.source="{ item }">
                                            <v-chip
                                                :color="getSourceColor(item.source)"
                                                text-color="white"
                                                small
                                            >
                                                <strong>
                                                    {{ formatSourceName(item.source) }}
                                                </strong>
                                            </v-chip>
                                        </template>

                                        <template #no-data>
                                            <div class="text-center pa-8">
                                                No recent activity found
                                            </div>
                                        </template>
                                    </v-data-table>
                                </div>
                            </div>
                        </v-col>

                        <v-col
                            cols="12"
                            lg="4"
                        >
                            <div class="chart-container rounded-lg">
                                <div class="d-flex justify-space-between align-baseline mb-5">
                                    <h3 class="font-weight-bold text-h6 mb-0">Consent Sources</h3>
                                    <div class="d-flex align-center">
                                        <span class="text-body-2 mr-1">{{ currentConsentSourceRangeLabel }}</span>
                                        <v-btn
                                            icon
                                            small
                                            color="primary"
                                            aria-label="Edit Consent Source Range"
                                            @click="openConsentSourceRangeDialog"
                                        >
                                            <v-icon
                                                aria-hidden="true"
                                                small
                                            >mdi-pencil</v-icon>
                                        </v-btn>
                                    </div>
                                </div>
                                <m-chart-donut
                                    title=""
                                    :labels="consentSourceLabels"
                                    :data="consentSourceData"
                                    :colors="consentSourceColors"
                                    :table_headers="consentSourceTableHeaders"
                                    :table_rows="consentSourceTableRows"
                                />
                            </div>
                        </v-col>
                    </v-row>
                </section>

                <!-- Custom Date Range Dialog -->
                <v-dialog
                    v-model="customDateRangeDialog"
                    max-width="600px"
                    persistent
                >
                    <v-card class="custom-range-dialog">
                        <v-card-title class="d-flex align-center justify-space-between mb-2">
                            <span class="text-h6">
                                Select Date Range
                                <span class="text-caption grey--text">
                                    ({{ activeChartForCustomRange === 'timeSeries' ? 'Opt-ins Trend' : 'Consent Sources'
                                    }})
                                </span>
                            </span>
                            <v-btn
                                icon
                                small
                                @click="cancelCustomRange"
                            >
                                <v-icon>mdi-close</v-icon>
                            </v-btn>
                        </v-card-title>

                        <v-card-text class="pb-0">
                            <!-- Date Range Options -->
                            <div class="mb-4">
                                <div class="d-flex flex-wrap gap-2">
                                    <v-chip
                                        v-for="preset in presetRanges"
                                        :key="preset.value"
                                        small
                                        :outlined="!isPresetSelected(preset.value)"
                                        :color="isPresetSelected(preset.value) ? 'primary' : ''"
                                        @click="selectPresetRange(preset.value)"
                                        class="preset-range-chip"
                                    >
                                        {{ preset.label }}
                                    </v-chip>

                                    <!-- Custom option -->
                                    <v-chip
                                        small
                                        :outlined="tempSelection.type !== 'custom'"
                                        :color="tempSelection.type === 'custom' ? 'primary' : ''"
                                        @click="selectCustomRange"
                                        class="preset-range-chip"
                                    >
                                        Custom
                                    </v-chip>
                                </div>
                            </div>

                            <!-- Custom Date Range Section - Only shows when Custom is selected -->
                            <div
                                v-if="tempSelection.type === 'custom'"
                                class="mt-6 mb-4"
                                key="custom-date-inputs"
                            >
                                <v-row>
                                    <v-col cols="6">
                                        <v-text-field
                                            v-model="tempSelection.customStart"
                                            label="Start Date"
                                            type="date"
                                            outlined
                                            dense
                                            hide-details="auto"
                                            no-title
                                            horizontal
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <v-text-field
                                            v-model="tempSelection.customEnd"
                                            label="End Date"
                                            type="date"
                                            outlined
                                            dense
                                            hide-details="auto"
                                            no-title
                                        />
                                    </v-col>
                                </v-row>
                            </div>
                        </v-card-text>

                        <v-card-text class="pt-0 pb-2 mb-2">
                            <v-checkbox
                                v-model="tempSelection.applyToAllCharts"
                                label="Apply this selection to all charts on the page"
                                class="mt-0"
                                dense
                                hide-details
                            />
                        </v-card-text>

                        <v-card-actions class="px-6 pb-4">
                            <v-spacer />
                            <v-btn
                                text
                                @click="cancelCustomRange"
                            >
                                Cancel
                            </v-btn>
                            <v-btn
                                color="primary"
                                :disabled="tempSelection.type === 'custom' && !isCustomRangeValid"
                                @click="applySelection"
                            >
                                Apply Selection
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
            </div>
        </v-container>
    </m-pageframe>
</template>

<script>
import { DateTime } from 'luxon';
import { ANALYTICS_COLORS } from '@/js/analytics-colors.js';
import LocalStorage from '@/js/LocalStorage.js';

export default {
    name: "AnalyticsOptInDemo",

    data() {
        return {
            localStorage: new LocalStorage(),
            STORAGE_KEYS: {
                TIME_SERIES_TIMEFRAME: 'analytics_opt_in_time_series_timeframe',
                CONSENT_SOURCE_TIMEFRAME: 'analytics_opt_in_consent_source_timeframe',
                TIME_SERIES_CUSTOM_RANGE: 'analytics_opt_in_time_series_custom_range',
                CONSENT_SOURCE_CUSTOM_RANGE: 'analytics_opt_in_consent_source_custom_range'
            },

            downloadLoading: false,
            recentOptInsLoading: false,

            // current selections for each chart
            timeSeriesTimeframe: 'last30Days',
            consentSourceTimeframe: 'last30Days',

            // preset ranges available in dialog
            presetRanges: [
                { label: 'Last 7 Days', value: 'last7Days' },
                { label: 'Last 30 Days', value: 'last30Days' },
                { label: 'Year to Date', value: 'yearToDate' }
            ],

            // custom date range state
            customDateRangeDialog: false,
            activeChartForCustomRange: null, // 'timeSeries' or 'consentSource'

            // temporary selection state for dialog (not applied until confirmed)
            tempSelection: {
                type: 'preset', // 'preset' or 'custom'
                presetValue: null,
                customStart: '',
                customEnd: '',
                applyToAllCharts: false
            },

            timeSeriesCustomRange: {
                start: DateTime.now().minus({ months: 3 }).toFormat('yyyy-MM-dd'),
                end: DateTime.now().toFormat('yyyy-MM-dd'),
                applied: false
            },
            consentSourceCustomRange: {
                start: DateTime.now().minus({ months: 3 }).toFormat('yyyy-MM-dd'),
                end: DateTime.now().toFormat('yyyy-MM-dd'),
                applied: false
            },

            // didn't end up being necessary, but leaving in place for potential future demo states
            currentDemoState: 'growth',
            demoStates: {
                growth: {
                    label: 'Growth Trend',
                    description: 'Growth trend with positive metrics'
                },
                declining: {
                    label: 'Declining Trend',
                    description: 'Recent decline in opt-ins'
                },
                flat: {
                    label: 'Flat Growth',
                    description: 'Minimal growth, stable metrics'
                },
                volatile: {
                    label: 'Volatile Pattern',
                    description: 'Inconsistent ups and downs'
                },
                noData: {
                    label: 'Minimal Data',
                    description: 'Newer account with minimal data'
                }
            },

            // base mock data (will be modified by demo states)
            baseTotalConsentCount: 12847,
            baseOptInsLast30Days: 342,
            baseOptInsYearToDate: 4156,
            baseMonthToDateOptIns: 178,
            lastUpdated: DateTime.now().toFormat('MMM dd, yyyy h:mm a'),

            // mock data - consent sources pie chart
            consentSourceLabels: ['Widget', 'Text', 'Manual', 'Keyword', 'Form Connect', 'Integration', 'Other'],
            baseConsentSourceDataDaily: [24, 18, 12, 8, 6, 4, 3],
            baseConsentSourceDataWeekly: [156, 98, 67, 45, 38, 32, 8],
            baseConsentSourceDataMonthly: [1234, 851, 620, 456, 403, 287, 96],
            baseConsentSourceDataYearly: [8967, 6234, 4321, 3987, 2876, 2145, 543],

            // mock data - time series base data
            baseTimeSeriesLabelsDaily: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
            baseTimeSeriesLabelsWeekly: ['12w ago', '11w ago', '10w ago', '9w ago', '8w ago', '7w ago', '6w ago', '5w ago', '4w ago', '3w ago', '2w ago', 'Last week'],
            baseTimeSeriesLabelsMonthly: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
            baseTimeSeriesLabelsYearly: ['2021', '2022', '2023', '2024', '2025'],

            // mock data - recent opt-ins table
            recentOptInsHeaders: [
                { text: 'Name', value: 'name', sortable: true },
                { text: 'Phone Number', value: 'phone', sortable: false },
                { text: 'Date & Time', value: 'datetime', sortable: true },
                { text: 'Source', value: 'source', sortable: true }
            ],
            recentOptIns: this.generateMockOptIns()
        };
    },

    computed: {
        totalConsentCount() {
            const multipliers = {
                growth: 1.0,
                declining: 0.85,
                flat: 0.95,
                volatile: 1.1,
                noData: 0.02
            };
            return Math.round(this.baseTotalConsentCount * multipliers[this.currentDemoState]);
        },

        optInsLast30Days() {
            const multipliers = {
                growth: 1.0,
                declining: 0.6,
                flat: 0.9,
                volatile: 1.2,
                noData: 0.03
            };
            return Math.round(this.baseOptInsLast30Days * multipliers[this.currentDemoState]);
        },

        optInsYearToDate() {
            const multipliers = {
                growth: 1.0,
                declining: 0.8,
                flat: 0.95,
                volatile: 1.05,
                noData: 0.02
            };
            return Math.round(this.baseOptInsYearToDate * multipliers[this.currentDemoState]);
        },

        monthToDateOptIns() {
            const multipliers = {
                growth: 1.0,
                declining: 0.4,
                flat: 0.85,
                volatile: 1.3,
                noData: 0.06
            };
            return Math.round(this.baseMonthToDateOptIns * multipliers[this.currentDemoState]);
        },

        monthToDateGrowth() {
            const growthValues = {
                growth: 12.4,
                declining: -8.2,
                flat: 0.8,
                volatile: 24.1,
                noData: 0.0
            };
            return growthValues[this.currentDemoState];
        },

        timeSeriesLabels() {
            if (this.timeSeriesTimeframe === 'last7Days') {
                return this.baseTimeSeriesLabelsDaily;
            } else if (this.timeSeriesTimeframe === 'last30Days') {
                // weekly aggregation for last 30 days to avoid clutter
                const labels = [];
                const now = DateTime.now();
                const weeksToShow = 5;

                for (let i = weeksToShow - 1; i >= 0; i--) {
                    const weekStart = now.minus({ weeks: i }).startOf('week');
                    if (i === 0) {
                        labels.push('This Week');
                    } else if (i === 1) {
                        labels.push('Last Week');
                    } else {
                        labels.push(weekStart.toFormat('MMM dd'));
                    }
                }
                return labels;
            } else if (this.timeSeriesTimeframe === 'yearToDate') {
                // YTD monthly labels
                const labels = [];
                const now = DateTime.now();
                const startOfYear = now.startOf('year');
                let current = startOfYear;
                while (current <= now) {
                    labels.push(current.toFormat('MMM'));
                    current = current.plus({ months: 1 });
                }
                return labels;
            } else if (this.timeSeriesTimeframe === 'custom') {
                return this.generateCustomRangeLabels('timeSeries');
            } else {
                return this.baseTimeSeriesLabelsDaily;
            }
        },

        timeSeriesData() {
            const patterns7Days = {
                growth: [12, 18, 15, 22, 28, 31, 24],
                declining: [31, 28, 22, 15, 18, 12, 8],
                flat: [18, 20, 19, 21, 18, 20, 19],
                volatile: [8, 32, 15, 28, 11, 25, 19],
                noData: [0, 0, 1, 0, 2, 1, 3]
            };

            const patterns30Days = {
                growth: [89, 134, 156, 189, 218],
                declining: [218, 189, 156, 134, 89],
                flat: [145, 148, 152, 149, 146],
                volatile: [98, 245, 127, 203, 164],
                noData: [3, 8, 12, 18, 25]
            };

            const patternsYearToDate = {
                growth: [245, 298, 342, 378, 421, 456, 489, 523],
                declining: [523, 489, 456, 421, 378, 342, 298, 245],
                flat: [345, 352, 348, 356, 351, 349, 354, 347],
                volatile: [234, 456, 189, 567, 298, 634, 312, 589],
                noData: [5, 8, 12, 18, 25, 34, 42, 56]
            };

            if (this.timeSeriesTimeframe === 'last7Days') {
                return patterns7Days[this.currentDemoState];
            } else if (this.timeSeriesTimeframe === 'last30Days') {
                return patterns30Days[this.currentDemoState];
            } else if (this.timeSeriesTimeframe === 'yearToDate') {
                return patternsYearToDate[this.currentDemoState];
            } else if (this.timeSeriesTimeframe === 'custom') {
                return this.generateCustomRangeData('timeSeries');
            } else {
                return patterns7Days[this.currentDemoState];
            }
        },

        consentSourceData() {
            let baseData;
            if (this.consentSourceTimeframe === 'last7Days') {
                baseData = this.baseConsentSourceDataDaily;
            } else if (this.consentSourceTimeframe === 'last30Days') {
                baseData = this.baseConsentSourceDataDaily; // use daily as base for 30 days
            } else if (this.consentSourceTimeframe === 'yearToDate') {
                baseData = this.baseConsentSourceDataMonthly;
            } else if (this.consentSourceTimeframe === 'custom') {
                baseData = this.generateCustomRangeConsentData('consentSource');
            } else {
                baseData = this.baseConsentSourceDataDaily;
            }

            const multipliers = {
                growth: 1.0,
                declining: 0.75,
                flat: 0.95,
                volatile: 1.15,
                noData: 0.01
            };
            return baseData.map(value =>
                Math.round(value * multipliers[this.currentDemoState])
            );
        },

        monthToDateGrowthFormatted() {
            const growth = this.monthToDateGrowth;
            const sign = growth > 0 ? '+' : '';
            return `${sign}${growth}%`;
        },

        consentSourceTableHeaders() {
            return [
                { text: 'Source', value: 'label', sortable: true },
                { text: 'Count', value: 'value', sortable: true },
                { text: 'Percentage', value: 'percent', sortable: true }
            ];
        },

        consentSourceTableRows() {
            const total = this.consentSourceData.reduce((sum, val) => sum + val, 0);
            return this.consentSourceLabels.map((label, index) => ({
                label: label,
                value: this.consentSourceData[index],
                percent: ((this.consentSourceData[index] / total) * 100).toFixed(1)
            }));
        },

        consentSourceColors() {
            const labelToSourceKey = {
                'Widget': 'widget',
                'Text': 'text',
                'Manual': 'manual',
                'Keyword': 'keyword',
                'Form Connect': 'form-connect',
                'Integration': 'integration',
                'Other': 'other'
            };

            const sourceKeyToColor = {
                'widget': ANALYTICS_COLORS.blue,
                'text': ANALYTICS_COLORS.green,
                'manual': ANALYTICS_COLORS.orange,
                'keyword': ANALYTICS_COLORS.purple,
                'form-connect': ANALYTICS_COLORS.teal,
                'integration': ANALYTICS_COLORS.indigo,
                'other': ANALYTICS_COLORS.grey
            };

            return this.consentSourceLabels.map(label => {
                const sourceKey = labelToSourceKey[label];
                return sourceKeyToColor[sourceKey] || ANALYTICS_COLORS.grey;
            });
        },

        isCustomRangeValid() {
            if (!this.tempSelection.customStart || !this.tempSelection.customEnd) {
                return false;
            }
            const start = DateTime.fromISO(this.tempSelection.customStart);
            const end = DateTime.fromISO(this.tempSelection.customEnd);
            return start.isValid && end.isValid && start <= end;
        },

        currentCustomRange() {
            return this.activeChartForCustomRange === 'timeSeries'
                ? this.timeSeriesCustomRange
                : this.consentSourceCustomRange;
        },

        otherChartHasCustomRange() {
            const otherChart = this.activeChartForCustomRange === 'timeSeries'
                ? this.consentSourceCustomRange
                : this.timeSeriesCustomRange;
            return otherChart.applied;
        },

        otherChartName() {
            return this.activeChartForCustomRange === 'timeSeries'
                ? 'Consent Sources'
                : 'Opt-ins Trend';
        },

        otherChartRangeLabel() {
            const otherChart = this.activeChartForCustomRange === 'timeSeries'
                ? this.consentSourceCustomRange
                : this.timeSeriesCustomRange;
            if (!otherChart.applied) return '';
            const start = DateTime.fromISO(otherChart.start);
            const end = DateTime.fromISO(otherChart.end);
            const currentYear = DateTime.now().year;

            const startYear = start.year;
            const endYear = end.year;
            const includeYear = startYear !== currentYear || endYear !== currentYear;

            if (includeYear) {
                if (startYear === endYear) {
                    // same year, show year once at the end
                    return `${start.toFormat('MMM dd')} - ${end.toFormat('MMM dd, yyyy')}`;
                } else {
                    // different years, show year for both
                    return `${start.toFormat('MMM dd, yyyy')} - ${end.toFormat('MMM dd, yyyy')}`;
                }
            } else {
                return `${start.toFormat('MMM dd')} - ${end.toFormat('MMM dd')}`;
            }
        },

        timeSeriesCustomRangeLabel() {
            if (!this.timeSeriesCustomRange.applied) return 'Custom Range';
            const start = DateTime.fromISO(this.timeSeriesCustomRange.start);
            const end = DateTime.fromISO(this.timeSeriesCustomRange.end);
            const currentYear = DateTime.now().year;

            const startYear = start.year;
            const endYear = end.year;
            const includeYear = startYear !== currentYear || endYear !== currentYear;

            if (includeYear) {
                if (startYear === endYear) {
                    // same year, show year once at the end
                    return `${start.toFormat('MMM dd')} - ${end.toFormat('MMM dd, yyyy')}`;
                } else {
                    // different years, show year for both
                    return `${start.toFormat('MMM dd, yyyy')} - ${end.toFormat('MMM dd, yyyy')}`;
                }
            } else {
                return `${start.toFormat('MMM dd')} - ${end.toFormat('MMM dd')}`;
            }
        },

        consentSourceCustomRangeLabel() {
            if (!this.consentSourceCustomRange.applied) return 'Custom Range';
            const start = DateTime.fromISO(this.consentSourceCustomRange.start);
            const end = DateTime.fromISO(this.consentSourceCustomRange.end);
            const currentYear = DateTime.now().year;

            const startYear = start.year;
            const endYear = end.year;
            const includeYear = startYear !== currentYear || endYear !== currentYear;

            if (includeYear) {
                if (startYear === endYear) {
                    // same year, show year once at the end
                    return `${start.toFormat('MMM dd')} - ${end.toFormat('MMM dd, yyyy')}`;
                } else {
                    // different years, show year for both
                    return `${start.toFormat('MMM dd, yyyy')} - ${end.toFormat('MMM dd, yyyy')}`;
                }
            } else {
                return `${start.toFormat('MMM dd')} - ${end.toFormat('MMM dd')}`;
            }
        },

        currentTimeSeriesRangeLabel() {
            if (this.timeSeriesTimeframe === 'custom' && this.timeSeriesCustomRange.applied) {
                return this.timeSeriesCustomRangeLabel;
            }
            const preset = this.presetRanges.find(p => p.value === this.timeSeriesTimeframe);
            return preset ? preset.label : 'Last 7 Days';
        },

        currentConsentSourceRangeLabel() {
            if (this.consentSourceTimeframe === 'custom' && this.consentSourceCustomRange.applied) {
                return this.consentSourceCustomRangeLabel;
            }
            const preset = this.presetRanges.find(p => p.value === this.consentSourceTimeframe);
            return preset ? preset.label : 'Last 7 Days';
        }
    },

    mounted() {
        this.loadPreferencesFromStorage();
    },

    methods: {
        openTimeSeriesRangeDialog() {
            this.activeChartForCustomRange = 'timeSeries';
            this.initializeTempSelection();
            this.customDateRangeDialog = true;
        },

        openConsentSourceRangeDialog() {
            this.activeChartForCustomRange = 'consentSource';
            this.initializeTempSelection();
            this.customDateRangeDialog = true;
        },

        initializeTempSelection() {
            const currentSelection = this.activeChartForCustomRange === 'timeSeries'
                ? this.timeSeriesTimeframe
                : this.consentSourceTimeframe;

            if (currentSelection === 'custom') {
                const customRange = this.activeChartForCustomRange === 'timeSeries'
                    ? this.timeSeriesCustomRange
                    : this.consentSourceCustomRange;
                this.tempSelection = {
                    type: 'custom',
                    presetValue: null,
                    customStart: customRange.start,
                    customEnd: customRange.end,
                    applyToAllCharts: false
                };
            } else {
                this.tempSelection = {
                    type: 'preset',
                    presetValue: currentSelection,
                    customStart: DateTime.now().minus({ months: 3 }).toFormat('yyyy-MM-dd'),
                    customEnd: DateTime.now().toFormat('yyyy-MM-dd'),
                    applyToAllCharts: false
                };
            }
        },

        isPresetSelected(presetValue) {
            return this.tempSelection.type === 'preset' && this.tempSelection.presetValue === presetValue;
        },

        selectPresetRange(presetValue) {
            this.tempSelection = {
                type: 'preset',
                presetValue: presetValue,
                customStart: this.tempSelection.customStart,
                customEnd: this.tempSelection.customEnd,
                applyToAllCharts: this.tempSelection.applyToAllCharts
            };
        },

        selectCustomRange() {
            this.tempSelection.type = 'custom';
        },

        loadPreferencesFromStorage() {
            const savedTimeSeriesTimeframe = this.localStorage.get(this.STORAGE_KEYS.TIME_SERIES_TIMEFRAME, null);
            const savedConsentSourceTimeframe = this.localStorage.get(this.STORAGE_KEYS.CONSENT_SOURCE_TIMEFRAME, null);

            if (savedTimeSeriesTimeframe) {
                this.timeSeriesTimeframe = savedTimeSeriesTimeframe;
            }

            if (savedConsentSourceTimeframe) {
                this.consentSourceTimeframe = savedConsentSourceTimeframe;
            }

            const savedTimeSeriesCustomRange = this.localStorage.get(this.STORAGE_KEYS.TIME_SERIES_CUSTOM_RANGE, null);
            const savedConsentSourceCustomRange = this.localStorage.get(this.STORAGE_KEYS.CONSENT_SOURCE_CUSTOM_RANGE, null);

            if (savedTimeSeriesCustomRange) {
                this.timeSeriesCustomRange = savedTimeSeriesCustomRange;
            }

            if (savedConsentSourceCustomRange) {
                this.consentSourceCustomRange = savedConsentSourceCustomRange;
            }
        },

        savePreferencesToStorage() {
            this.localStorage.set(this.STORAGE_KEYS.TIME_SERIES_TIMEFRAME, this.timeSeriesTimeframe);
            this.localStorage.set(this.STORAGE_KEYS.CONSENT_SOURCE_TIMEFRAME, this.consentSourceTimeframe);
            this.localStorage.set(this.STORAGE_KEYS.TIME_SERIES_CUSTOM_RANGE, this.timeSeriesCustomRange);
            this.localStorage.set(this.STORAGE_KEYS.CONSENT_SOURCE_CUSTOM_RANGE, this.consentSourceCustomRange);
        },

        generateMockOptIns() {
            const today = DateTime.now();
            const yesterday = today.minus({ days: 1 });

            const mockNames = [
                'Sarah Johnson', 'Michael Chen', 'Emily Rodriguez', 'David Thompson',
                'Jessica Lee', 'Robert Wilson', 'Amanda Davis', 'Christopher Brown'
            ];

            const mockPhones = [
                '+15551234567', '+15559876543', '+15555551234', '+15554567890',
                '+15553456789', '+15552345678', '+15551357924', '+15559753124'
            ];

            const sources = ['widget', 'text', 'manual', 'keyword', 'form-connect', 'integration'];

            if (this.currentDemoState === 'noData') {
                return mockNames.slice(0, 2).map((name, index) => {
                    const baseDate = today;
                    const randomHour = Math.floor(Math.random() * 4) + 14; // afternoon hours
                    const randomMinute = Math.floor(Math.random() * 60);
                    const randomSecond = Math.floor(Math.random() * 60);

                    const dateTime = baseDate.set({
                        hour: randomHour,
                        minute: randomMinute,
                        second: randomSecond
                    });

                    return {
                        name: name,
                        phone: mockPhones[index],
                        datetime: dateTime.toISO(),
                        source: sources[index % sources.length]
                    };
                });
            }

            return mockNames.map((name, index) => {
                // first 5 entries from today, last 3 from yesterday
                const isToday = index < 5;
                const baseDate = isToday ? today : yesterday;

                // random times throughout day - 8a to 7p range
                const randomHour = Math.floor(Math.random() * 12) + 8;
                const randomMinute = Math.floor(Math.random() * 60);
                const randomSecond = Math.floor(Math.random() * 60);

                const dateTime = baseDate.set({
                    hour: randomHour,
                    minute: randomMinute,
                    second: randomSecond
                });

                return {
                    name: name,
                    phone: mockPhones[index],
                    datetime: dateTime.toISO(),
                    source: sources[index % sources.length]
                };
            });
        },

        setDemoState(stateKey) {
            this.currentDemoState = stateKey;
            this.recentOptIns = this.generateMockOptIns();
        },

        formatSourceName(source) {
            const sourceNames = {
                widget: 'Widget',
                text: 'Text Message',
                manual: 'Manual Entry',
                keyword: 'Keyword',
                'form-connect': 'Form Connect',
                integration: 'Integration',
                other: 'Other'
            };
            return sourceNames[source] || source.charAt(0).toUpperCase() + source.slice(1);
        },

        refreshRecentOptIns() {
            this.recentOptInsLoading = true;
            setTimeout(() => {
                this.recentOptInsLoading = false;
            }, 1000);
        },

        downloadConsentRegister() {
            this.downloadLoading = true;

            this.$rc.excelExport(
                'EXPORT: /api/v1/bulk/opt-outs/export-current-state/:organization_id',
                {
                    organization_id: this.$route.params.organization_id,
                }
            );

            setTimeout(() => {
                this.downloadLoading = false;
            }, 2000);
        },

        getSourceColor(source) {
            const colors = {
                widget: 'blue',
                text: 'green',
                manual: 'orange',
                keyword: 'purple',
                'form-connect': 'teal',
                integration: 'indigo',
                typeform: 'amber', // deprecated but keeping for now
                other: 'grey'
            };
            return colors[source] || 'grey';
        },

        // TODO: if we stick with this, move to $rc prototype or imported es module for reuse
        getHumanFriendlyDate(dateString) {
            const date = DateTime.fromISO(dateString);
            const today = DateTime.now().startOf('day');
            const yesterday = today.minus({ days: 1 });
            const itemDate = date.startOf('day');

            if (itemDate.equals(today)) {
                return 'Today';
            } else if (itemDate.equals(yesterday)) {
                return 'Yesterday';
            } else {
                return this.$rc.pretty(dateString, 'date');
            }
        },

        setQuickRange(range) {
            const now = DateTime.now();

            switch (range) {
                case 'lastWeek':
                    this.tempSelection.customStart = now.minus({ weeks: 1 }).toFormat('yyyy-MM-dd');
                    this.tempSelection.customEnd = now.toFormat('yyyy-MM-dd');
                    break;
                case 'lastMonth':
                    this.tempSelection.customStart = now.minus({ months: 1 }).toFormat('yyyy-MM-dd');
                    this.tempSelection.customEnd = now.toFormat('yyyy-MM-dd');
                    break;
                case 'last3Months':
                    this.tempSelection.customStart = now.minus({ months: 3 }).toFormat('yyyy-MM-dd');
                    this.tempSelection.customEnd = now.toFormat('yyyy-MM-dd');
                    break;
                case 'lastYear':
                    this.tempSelection.customStart = now.minus({ years: 1 }).toFormat('yyyy-MM-dd');
                    this.tempSelection.customEnd = now.toFormat('yyyy-MM-dd');
                    break;
            }
            this.tempSelection.type = 'custom';
        },

        syncWithOtherChart() {
            const otherChart = this.activeChartForCustomRange === 'timeSeries'
                ? this.consentSourceCustomRange
                : this.timeSeriesCustomRange;

            this.tempSelection.customStart = otherChart.start;
            this.tempSelection.customEnd = otherChart.end;
            this.tempSelection.type = 'custom';
        },

        applySelection() {
            if (this.tempSelection.applyToAllCharts) {
                if (this.tempSelection.type === 'preset') {
                    this.timeSeriesTimeframe = this.tempSelection.presetValue;
                    this.consentSourceTimeframe = this.tempSelection.presetValue;
                } else if (this.tempSelection.type === 'custom') {
                    if (!this.isCustomRangeValid) return;

                    const customRangeData = {
                        start: this.tempSelection.customStart,
                        end: this.tempSelection.customEnd,
                        applied: true
                    };

                    this.timeSeriesCustomRange = { ...customRangeData };
                    this.consentSourceCustomRange = { ...customRangeData };
                    this.timeSeriesTimeframe = 'custom';
                    this.consentSourceTimeframe = 'custom';
                }
            } else {
                if (this.tempSelection.type === 'preset') {
                    if (this.activeChartForCustomRange === 'timeSeries') {
                        this.timeSeriesTimeframe = this.tempSelection.presetValue;
                    } else {
                        this.consentSourceTimeframe = this.tempSelection.presetValue;
                    }
                } else if (this.tempSelection.type === 'custom') {
                    if (!this.isCustomRangeValid) return;

                    const customRangeData = {
                        start: this.tempSelection.customStart,
                        end: this.tempSelection.customEnd,
                        applied: true
                    };

                    if (this.activeChartForCustomRange === 'timeSeries') {
                        this.timeSeriesCustomRange = { ...customRangeData };
                        this.timeSeriesTimeframe = 'custom';
                    } else {
                        this.consentSourceCustomRange = { ...customRangeData };
                        this.consentSourceTimeframe = 'custom';
                    }
                }
            }

            this.savePreferencesToStorage();
            this.customDateRangeDialog = false;
        }, cancelCustomRange() {
            this.customDateRangeDialog = false;
            // no need to reset anything since we're not automatically changing selections
        }, generateCustomRangeLabels(chartType) {
            const customRange = chartType === 'timeSeries' ? this.timeSeriesCustomRange : this.consentSourceCustomRange;
            const start = DateTime.fromISO(customRange.start);
            const end = DateTime.fromISO(customRange.end);
            const diffInDays = end.diff(start, 'days').days;

            // intelligent data aggregation based on range duration
            if (diffInDays <= 7) {
                // daily labels for ranges <= 7 days
                const labels = [];
                let current = start;
                while (current <= end) {
                    if (current.hasSame(DateTime.now(), 'day')) {
                        labels.push('Today');
                    } else if (current.hasSame(DateTime.now().minus({ days: 1 }), 'day')) {
                        labels.push('Yesterday');
                    } else {
                        labels.push(current.toFormat('MMM dd'));
                    }
                    current = current.plus({ days: 1 });
                }
                return labels;
            } else if (diffInDays <= 60) {
                // weekly labels for ranges 8-60 days (show max 8-9 weeks)
                const labels = [];
                let current = start.startOf('week');
                const now = DateTime.now();

                while (current <= end) {
                    if (current.hasSame(now.startOf('week'), 'week')) {
                        labels.push('This Week');
                    } else if (current.hasSame(now.minus({ weeks: 1 }).startOf('week'), 'week')) {
                        labels.push('Last Week');
                    } else {
                        labels.push(current.toFormat('MMM dd'));
                    }
                    current = current.plus({ weeks: 1 });
                }
                return labels.slice(-10); // max 10 points for readability
            } else if (diffInDays <= 365) {
                // monthly labels for ranges 61-365 days (show max 12 months)
                const labels = [];
                let current = start.startOf('month');

                while (current <= end) {
                    if (current.year === DateTime.now().year) {
                        labels.push(current.toFormat('MMM'));
                    } else {
                        labels.push(current.toFormat('MMM yy'));
                    }
                    current = current.plus({ months: 1 });
                }
                return labels.slice(-12); // max 12 points for readability
            } else {
                // quarterly labels for longer ranges
                const labels = [];
                let current = start.startOf('quarter');

                while (current <= end) {
                    const quarter = Math.ceil(current.month / 3);
                    const year = current.year;
                    labels.push(`Q${quarter} ${year}`);
                    current = current.plus({ quarters: 1 });
                }
                return labels.slice(-16); // max 16 quarters for readability
            }
        },

        generateCustomRangeData(chartType) {
            const labels = this.generateCustomRangeLabels(chartType);
            const customRange = chartType === 'timeSeries' ? this.timeSeriesCustomRange : this.consentSourceCustomRange;
            const start = DateTime.fromISO(customRange.start);
            const end = DateTime.fromISO(customRange.end);
            const diffInDays = end.diff(start, 'days').days;

            let baseValue;
            if (diffInDays <= 7) {
                baseValue = 25; // daily
            } else if (diffInDays <= 60) {
                baseValue = 150; // weekly aggregated
            } else if (diffInDays <= 365) {
                baseValue = 600; // monthly aggregated
            } else {
                baseValue = 1800; // quarterly aggregated
            }

            const patterns = {
                growth: () => baseValue * (0.8 + Math.random() * 0.4) * (1 + Math.random() * 0.3),
                declining: () => baseValue * (0.8 + Math.random() * 0.4) * (1 - Math.random() * 0.3),
                flat: () => baseValue * (0.9 + Math.random() * 0.2),
                volatile: () => baseValue * (0.3 + Math.random() * 1.4),
                noData: () => Math.random() * (baseValue * 0.1)
            };

            return labels.map(() => Math.round(patterns[this.currentDemoState]()));
        },

        generateCustomRangeConsentData(chartType) {
            const customRange = chartType === 'consentSource' ? this.consentSourceCustomRange : this.timeSeriesCustomRange;
            const start = DateTime.fromISO(customRange.start);
            const end = DateTime.fromISO(customRange.end);
            const diffInDays = end.diff(start, 'days').days;

            let scaleFactor = 1;
            if (diffInDays <= 7) {
                scaleFactor = 0.2; // daily scale
            } else if (diffInDays <= 90) {
                scaleFactor = 1; // weekly scale
            } else if (diffInDays <= 365) {
                scaleFactor = 5; // monthly scale
            } else {
                scaleFactor = 20; // yearly scale
            }

            return this.baseConsentSourceDataWeekly.map(value =>
                Math.round(value * scaleFactor * (0.8 + Math.random() * 0.4))
            );
        }
    }
}
</script>

<style lang="scss" scoped>
.analytics-container {
    background: var(--color-bg-shaded-light);
    min-height: 100vh;
    padding: 2rem;
    padding-top: .5rem;

    .subcontainer {
        max-width: 120rem;
        margin-left: auto;
        margin-right: auto;
    }

    .demo-control {
        position: absolute;
        top: 0.5rem;
        right: 1.5rem;
        z-index: 1;

        .demo-btn {
            opacity: 0.7;
            font-size: 0.75rem;
            text-transform: none;
            letter-spacing: normal;

            &:hover {
                opacity: 1;
            }
        }
    }

    .metrics-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
        gap: 1.5rem;
        margin-bottom: 2.5rem;
    }

    .metric-card {
        background: var(--color-off-white);
        padding: 2rem 1.5rem;
        text-align: center;
        box-shadow: 0 0.125rem 0.75rem rgba(0, 0, 0, 0.08);
        transition: transform 0.2s ease, box-shadow 0.2s ease;

        &:hover {
            transform: translateY(-0.25rem);
            box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.12);
        }

        &.highlight {
            background: linear-gradient(135deg, var(--v-primary-lighten1) 0%, var(--v-primary-darken1) 100%);
            color: var(--color-off-white);

            .metric-number,
            .metric-label {
                color: var(--color-off-white);
            }
        }
    }

    .metric-number {
        font-size: 2.5rem;
        font-weight: 700;
        color: var(--v-primary-base);
        margin-bottom: 0.5rem;
        line-height: 1;
    }

    .metric-label {
        font-size: 0.875rem;
        color: #666;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    .chart-container {
        background: var(--color-off-white);
        padding: 1.5rem;
        box-shadow: 0 0.125rem 0.75rem rgba(0, 0, 0, 0.08);
        height: 100%;
    }

    .custom-range-dialog {
        .preset-range-chip {
            margin: 0.125rem;
            cursor: pointer;
            transition: all 0.2s ease;

            &:hover {
                background-color: var(--v-primary-lighten5);
                border-color: var(--v-primary-base);
            }
        }

        .quick-range-chip {
            margin: 0.125rem;
            cursor: pointer;
            transition: all 0.2s ease;

            &:hover {
                background-color: var(--v-primary-lighten5);
                border-color: var(--v-primary-base);
            }
        }

        .gap-2 {
            gap: 0.5rem;
        }
    }

    .recent-section {
        background: var(--color-off-white);
        padding: 1.5rem;
        box-shadow: 0 0.125rem 0.75rem rgba(0, 0, 0, 0.08);

        .recent-table-container {
            border-radius: 0.5rem;
            overflow: hidden;

            .recent-table {
                box-shadow: none !important;

                .v-data-table__wrapper {
                    border-radius: 0.5rem;
                }
            }
        }
    }

    @media (max-width: 960px) {
        .analytics-container {
            padding: 1rem;
        }

        .metrics-grid {
            grid-template-columns: repeat(auto-fit, minmax(12.5rem, 1fr));
            gap: 1rem;
        }

        .metric-card {
            padding: 1.5rem 1rem;
        }

        .metric-number {
            font-size: 2rem;
        }
    }
}
</style>
